import { Link } from "react-router-dom";
import { BarChart3, ArrowLeft } from "lucide-react";

const UPDATED = "April 20, 2026";

export default function Privacy() {
  return (
    <div className="min-h-screen bg-white text-slate-900" style={{ fontFamily: "Manrope" }}>
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="privacy-home-link">
            <div className="w-8 h-8 bg-blue-600 rounded text-white flex items-center justify-center">
              <BarChart3 className="w-4 h-4" />
            </div>
            <span className="font-semibold text-slate-900">RSOC × Hub</span>
          </Link>
          <Link to="/login" className="text-xs text-slate-500 hover:text-slate-900 flex items-center gap-1.5">
            <ArrowLeft className="w-3 h-3" /> Back to sign in
          </Link>
        </div>
      </header>

      <article className="max-w-3xl mx-auto px-6 py-12" data-testid="privacy-page">
        <h1 className="text-3xl font-semibold tracking-tight mb-2">Privacy Policy</h1>
        <p className="text-slate-500 mb-10 text-sm">Last updated: {UPDATED}</p>

        <style>{`
          .legal h2 { font-size: 1.125rem; font-weight: 600; margin-top: 2rem; margin-bottom: 0.5rem; color: #0f172a; }
          .legal h3 { font-size: 0.95rem; font-weight: 600; margin-top: 1.25rem; margin-bottom: 0.4rem; color: #334155; }
          .legal p { color: #475569; line-height: 1.7; margin-bottom: 0.75rem; font-size: 0.9rem; }
          .legal ul { list-style: disc; padding-left: 1.5rem; color: #475569; margin-bottom: 0.75rem; font-size: 0.9rem; line-height: 1.7; }
          .legal ul li { margin-bottom: 0.35rem; }
          .legal a { color: #2563eb; text-decoration: underline; }
          .legal table { width: 100%; border-collapse: collapse; margin: 1rem 0; font-size: 0.85rem; }
          .legal th, .legal td { border: 1px solid #e2e8f0; padding: 8px 10px; text-align: left; vertical-align: top; }
          .legal th { background: #f8fafc; font-weight: 600; color: #334155; }
          .legal strong { color: #0f172a; font-weight: 600; }
        `}</style>

        <div className="legal">        <h2>1. Who we are</h2>
        <p>
          RSOC × Hub is a private media-buying dashboard operated by its customer team (the "Operator"). This Privacy
          Policy explains what we collect from authorized users ("you") and from the advertising APIs you connect, how
          we use it, and your rights.
        </p>

        <h2>2. What we collect</h2>
        <h3>From you directly</h3>
        <ul>
          <li><strong>Account data</strong>: email, display name, bcrypt-hashed password, role, workspace.</li>
          <li><strong>API tokens & keys</strong> you paste into the Connections page: Facebook access token + ad account list, System1 auth_key + partner id, optional OpenAI / fal.ai / ElevenLabs / Resend keys, Slack/Discord webhook URLs. Stored encrypted at rest; never shared.</li>
          <li><strong>Creative briefs, campaign names, landing URLs</strong> you enter.</li>
          <li><strong>Uploaded media</strong> (buyer video clips, logo images) — stored on server disk and accessible only to your workspace.</li>
        </ul>

        <h3>From the APIs you connect</h3>
        <ul>
          <li><strong>Facebook Marketing API</strong>: campaign metadata, daily spend, impressions, clicks, conversions, creative objects, video IDs, pixel events. No end-user PII is stored (click-level data is hashed per CAPI spec).</li>
          <li><strong>System1 RSOC</strong>: revenue reports, click counts, keyword RPC, widget searches. Aggregate, no end-user PII.</li>
          <li><strong>Server-side click events</strong> forwarded to Meta CAPI are hashed (SHA-256) at source and logged anonymously for 30 days (quality monitoring); we never store raw IPs, user-agents, or end-user emails.</li>
        </ul>

        <h3>Automatically</h3>
        <ul>
          <li>Authentication events (login success / failure) with truncated IP for rate-limiting only.</li>
          <li>Application error telemetry (Sentry) with user PII stripped — only endpoint paths, stack traces, and release tags are sent.</li>
          <li>Approximate usage costs (LLM calls, video renders) for per-workspace cost tracking.</li>
        </ul>

        <h2>3. What we don't do</h2>
        <ul>
          <li>We never sell, rent, or share your API tokens or campaign data with third parties.</li>
          <li>We don't use your ad performance data to train AI models.</li>
          <li>We don't store end-user IP addresses, emails, or personal identifiers from your Facebook Pixel / CAPI events after hashing.</li>
          <li>We don't place any advertising or marketing cookies on this dashboard.</li>
        </ul>

        <h2>4. Third-party subprocessors</h2>
        <table>
          <thead><tr><th>Service</th><th>Purpose</th><th>Data shared</th></tr></thead>
          <tbody>
            <tr><td>Meta (Facebook Marketing API + CAPI)</td><td>Campaign management & conversion reporting</td><td>Your ad account ID, campaign IDs, hashed conversion events</td></tr>
            <tr><td>System1 Network</td><td>RSOC revenue reporting</td><td>Your System1 auth_key + partner_id</td></tr>
            <tr><td>Emergent Universal Key / OpenAI / Anthropic / fal.ai</td><td>AI text, image, and video generation</td><td>Your prompts and briefs (no user PII)</td></tr>
            <tr><td>ElevenLabs</td><td>Voiceover synthesis</td><td>Text scripts you submit</td></tr>
            <tr><td>Resend</td><td>Transactional email (digest, password reset, invites)</td><td>Recipient email + email body</td></tr>
            <tr><td>MongoDB (self-hosted)</td><td>Primary datastore</td><td>All application data listed above</td></tr>
            <tr><td>AWS S3 (optional)</td><td>Encrypted nightly database backups</td><td>AES-256 encrypted database dump</td></tr>
            <tr><td>Sentry (optional)</td><td>Error tracking</td><td>Stack traces, endpoint paths (PII stripped)</td></tr>
            <tr><td>Hetzner Cloud</td><td>Application hosting</td><td>All application data at rest</td></tr>
          </tbody>
        </table>

        <h2>5. How long we keep data</h2>
        <ul>
          <li>Account data: for the life of your account + 30 days after deletion.</li>
          <li>API tokens: until you overwrite them or we delete your account.</li>
          <li>Campaign metrics (daily rows): 18 months rolling.</li>
          <li>Postback & CAPI logs: 30 days (for EMQ quality monitoring), then auto-purged.</li>
          <li>Password-reset tokens: 1 hour (TTL-enforced in MongoDB).</li>
          <li>Sentry error events: follow Sentry's default retention (90 days).</li>
          <li>S3 backups: rolling 14 days (configurable via `BACKUP_RETENTION_DAYS`).</li>
        </ul>

        <h2>6. Your rights (GDPR / CCPA)</h2>
        <p>
          You may request access to, correction of, or deletion of your personal data by emailing the Operator at the
          address in your onboarding invitation. We respond within 30 days. You may also revoke any connected token
          directly from the source (Facebook Business Manager, System1 portal, etc.) at any time — this will immediately
          stop our ability to call that API on your behalf.
        </p>

        <h2>7. Security</h2>
        <ul>
          <li>All traffic is served over TLS (HTTPS) with HSTS enabled.</li>
          <li>Passwords are hashed with bcrypt; tokens are stored encrypted at rest.</li>
          <li>Failed-login brute force is rate-limited (10 attempts / 15 min per IP+email).</li>
          <li>Database backups are AES-256 encrypted in S3.</li>
          <li>Security headers (X-Content-Type-Options, Referrer-Policy, Permissions-Policy) are set by Caddy.</li>
          <li>We disclose any confirmed security incident affecting your data within 72 hours of discovery.</li>
        </ul>

        <h2>8. Children</h2>
        <p>The Service is not directed to children under 18 and does not knowingly collect data from them.</p>

        <h2>9. Changes</h2>
        <p>
          We may update this Policy. Material changes are posted here with a new "Last updated" date and, when required
          by law, notified by email.
        </p>

        <h2>10. Contact</h2>
        <p>
          For privacy requests, data export, or account deletion, email the Operator at the address in your onboarding
          invitation. See also our <Link to="/terms" className="text-blue-600 underline">Terms of Service</Link>.
        </p>

        <hr className="my-12" />
        <p className="text-xs text-slate-500">
          This policy is boilerplate drafted for an internal team-operated SaaS. Review with qualified legal counsel
          before opening the Service to external paying customers or submitting for Meta App Review — particularly the
          list of subprocessors and retention windows should match your actual vendor contracts.
        </p>
        </div>
      </article>
    </div>
  );
}
